export { getClientDispatcher } from './singleton';

import type { ServerToClientEvent } from '../types';

type AbortReason = { reason: string };
type AbortFunction = (arg: AbortReason) => void;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EventIngestedFunction = (response: any) => void;

/**
 * The glue between a {@link ServerToClientEvent} and a client-side component.
 *
 * This class is responsible for ingesting events from the server and dispatching them to the correct component.
 *
 * Components hoping to be called from the server must be registered. Today, that's a manual call a developer
 * needs to make. Tomorrow, that may be automatically generated by the compiler reading a code annotation.
 */

export class ClientDispatcher {
	private handlers: Map<string, EventIngestedFunction | AbortFunction>;

	private queuedPayloads: Map<string, ServerToClientEvent[]>;

	private abortReason?: AbortReason;

	private abortHandlers: Map<string, AbortFunction>;

	constructor() {
		this.handlers = new Map();
		this.abortHandlers = new Map();
		this.queuedPayloads = new Map();
	}

	/**
	 * Ingest a payload from the server.
	 * This will trigger a callback to the component that registered for this event, otherwise the payload will be queued to do so later.
	 * @param payload - The payload to ingest.
	 */
	ingest(payload: ServerToClientEvent): void {
		const handler = this.handlers.get(payload.id);
		if (handler) {
			this.exec(handler, payload);
		} else {
			const queue = this.queuedPayloads.get(payload.id) || [];
			queue.push(payload);
			this.queuedPayloads.set(payload.id, queue);
		}
	}

	/**
	 * Register a callback for a given event. If any payloads were queued for this event, they will be processed immediately.
	 * @param id - The event ID to register for.
	 * @param callback - The callback to trigger when the event is ingested.
	 */
	register(id: string, callback: EventIngestedFunction | AbortFunction): void {
		this.handlers.set(id, callback);

		// Process any queued payloads for this handler
		const queuedPayloads = this.queuedPayloads.get(id) || [];
		if (queuedPayloads.length > 0) {
			queuedPayloads.forEach((payload) => this.exec(callback, payload));
			this.queuedPayloads.delete(id);
		}
	}

	/**
	 * Unregister a callback for a given event.
	 * @param id
	 */
	unregister(id: string): void {
		this.handlers.delete(id);
		this.queuedPayloads.delete(id);
	}

	/**
	 * Register an abort handler that will get called if exceptional circmstances arise (such as SSR hanging up due to deadline exceeded)
	 * @param id - The event ID to register for.
	 * @param callback - The callback to trigger when abort detected.
	 */
	onAbort(id: string, callback: AbortFunction): void {
		if (this.abortReason) {
			this.exec(callback, { args: [this.abortReason] });
		} else {
			this.abortHandlers.set(id, callback);
		}
	}

	/**
	 * Notify any registered abort handlers that the server exceeded its deadline before finishing, so we may not have received all events.
	 * @private
	 * @param props
	 */
	_abort(props: AbortReason): void {
		this.abortReason = props;
		this.abortHandlers.forEach((handler) => {
			this.exec(handler, { args: [props] });
		});
		// We can delete these now that we've handled the case and remainder will be dealt with immediately.
		this.abortHandlers = new Map();
	}

	/**
	 * Execute a callback with the given payload.
	 * @param callback - The callback to execute.
	 * @param payload - The payload to execute the callback with.
	 */
	private exec(
		callback: EventIngestedFunction | AbortFunction,
		payload: Pick<ServerToClientEvent, 'args' | 'error'>,
	) {
		const { args, error } = payload;
		try {
			if (error) {
				throw error;
			}
			// @ts-expect-error
			callback(...args);
		} catch (err) {
			// TODO: log error
			// console.error(err);
		}
	}
}
